import { WELLNESS_DATA_MULTIPLIER } from '@/constants';
import { CONTENT_GC_TIME, CONTENT_STALE_TIME, FEED_PAGE_SIZE, SEARCH_MIN_LENGTH } from '@/constants/contentFeed';
import { sanitizeSearchQuery } from '@/utils/contentSearchQuery';
import queryKeys from '@/constants/queryKeys';
import { api } from '@/services/api';
import {
  getContentById,
  getContentFeed,
  getMyContent,
  getContentFilterOptions,
  getContentSearch,
  getFilteredContent,
  getFocusedFeed,
  getPersonalizedFeed,
  TGetFocusedFeedParams,
} from '@/services/api/requests/content';
import { TContentCreatorFacet, TContentDomain, TContentType, TPersonalizedFeedResponse } from '@/types/Content.types';
import { THealthDomainScores } from '@/types/Health.types';
import { TOrganizationDashboard } from '@/types/Organization.types';
import { infiniteQueryOptions, keepPreviousData, queryOptions } from '@tanstack/react-query';

// Shared so the route loader (prefetch on hover) and the detail page's useQuery
// use the identical key + fn — the loader's fetch becomes a cache hit on click.
export const getContentByIdOptions = (id: string) =>
  queryOptions({
    queryKey: [queryKeys.content.byId, id],
    queryFn: () => getContentById(id),
    staleTime: CONTENT_STALE_TIME,
    gcTime: CONTENT_GC_TIME,
  });

export type TContentFeedFilters = {
  domains?: TContentDomain[];
  subdomains?: string[];
  // true = MyOmnia platform content, false = creator/org content, undefined = both.
  isPlatformContent?: boolean;
  authorUserId?: string;
  type?: TContentType;
};

// Stable key per filter combination (sorted so order doesn't fragment the cache). `type`
// is single on the legacy feed and an array on the filtered/search surfaces — normalize
// both to a stable string.
const typeKey = (type?: TContentType | TContentType[]) =>
  Array.isArray(type) ? type.slice().sort().join(',') || 'all' : (type ?? 'all');

const feedFilterKey = (f: TContentFeedFilters | TFilteredContentFilters) => [
  f.domains?.slice().sort().join(',') ?? 'all',
  f.subdomains?.slice().sort().join(',') ?? 'all',
  f.isPlatformContent ?? 'all',
  f.authorUserId ?? 'all',
  typeKey(f.type),
];

// Infinite, filter-aware feed. `keepPreviousData` keeps the current grid visible
// while a new filter combination loads (no skeleton flash).
export const getContentFeedInfiniteOptions = (filters: TContentFeedFilters = {}) =>
  infiniteQueryOptions({
    queryKey: [queryKeys.content.feed, ...feedFilterKey(filters)],
    queryFn: ({ pageParam }) =>
      getContentFeed({ page: pageParam, limit: FEED_PAGE_SIZE, ...filters, sort: 'publishedAt:desc' }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const p = lastPage.pagination;
      if (!p) return undefined;
      return p.page < p.totalPages ? p.page + 1 : undefined;
    },
    staleTime: CONTENT_STALE_TIME,
    gcTime: CONTENT_GC_TIME,
    placeholderData: keepPreviousData,
  });

// The creator's own content — drafts, queued and published alike. Sorted by
// `updatedAt` rather than `publishedAt`: a draft has no `publishedAt`, so ordering by it
// would sink every unfinished piece below everything already published, which is the
// opposite of what a creator wants when looking at their own shelf.
export const MY_CONTENT_SORT = 'updatedAt:desc';

export const getMyContentInfiniteOptions = () =>
  infiniteQueryOptions({
    queryKey: [queryKeys.content.mine],
    queryFn: ({ pageParam }) =>
      getMyContent({ page: pageParam, limit: FEED_PAGE_SIZE, sort: MY_CONTENT_SORT }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const p = lastPage.pagination;
      if (!p) return undefined;
      return p.page < p.totalPages ? p.page + 1 : undefined;
    },
    staleTime: CONTENT_STALE_TIME,
    gcTime: CONTENT_GC_TIME,
  });

// --- Personalized feed algorithm surfaces ---

// One page of a cursor feed. The first page carries no cursor (the server mints the draw
// seed); later pages echo the opaque token back verbatim.
export type TFeedPageParam = { cursor?: string };

// The response carries a typed opaque `nextCursor`, shared by the ranked and focused
// feeds — and now the chronological/holdout arm too, which paginates like the rest. It
// is `null` only when there are no more results.
const readNextCursor = (page: TPersonalizedFeedResponse): string | undefined => {
  const next = page.nextCursor;

  return typeof next === 'string' && next.length > 0 ? next : undefined;
};

// Shared continuation for both cursor feeds (browse + focused): follow the cursor, stop
// when it's null. Never fall back to a page number — a request without a cursor asks the
// server for a *new* draw, so its "page 2" would be ordered against a different seed and
// produce duplicates/gaps.
const getFeedNextPageParam = (lastPage: TPersonalizedFeedResponse): TFeedPageParam | undefined => {
  const cursor = readNextCursor(lastPage);

  return cursor ? { cursor } : undefined;
};

// The ranked browse feed — a draw snapshot. Order is stable while scrolling one draw and
// only changes when the user explicitly refreshes (which mints a new server seed).
// `refetchOnWindowFocus`/`refetchOnReconnect` are off precisely because the defaults
// would silently reshuffle the grid under the user without any refresh.
//
// Unfiltered by design: applying any filter routes the grid to the deterministic
// /filtered surface instead, so the ranked feed never carries facets.
export const getPersonalizedFeedInfiniteOptions = () =>
  infiniteQueryOptions({
    queryKey: [queryKeys.content.personalizedFeed],
    queryFn: ({ pageParam }) => getPersonalizedFeed({ cursor: pageParam.cursor, limit: FEED_PAGE_SIZE }),
    initialPageParam: {} as TFeedPageParam,
    getNextPageParam: getFeedNextPageParam,
    staleTime: CONTENT_STALE_TIME,
    gcTime: CONTENT_GC_TIME,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

// The filtered/search surfaces accept multi-select `type` (unlike the legacy feed's single
// type), so override it to an array.
export type TFilteredContentFilters = Omit<TContentFeedFilters, 'type'> & {
  type?: TContentType[];
  creator?: TContentCreatorFacet;
};

// Deterministic faceted browse — same facets, same order, every time. Deliberately a
// different query key from the ranked feed so a deterministic result can never overwrite
// a live draw in the cache.
export const getFilteredContentInfiniteOptions = (filters: TFilteredContentFilters = {}) =>
  infiniteQueryOptions({
    queryKey: [queryKeys.content.filtered, ...feedFilterKey(filters), filters.creator ?? 'all'],
    queryFn: ({ pageParam }) => getFilteredContent({ page: pageParam, limit: FEED_PAGE_SIZE, ...filters }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const p = lastPage.pagination;
      if (!p) return undefined;
      return p.page < p.totalPages ? p.page + 1 : undefined;
    },
    staleTime: CONTENT_STALE_TIME,
    gcTime: CONTENT_GC_TIME,
    placeholderData: keepPreviousData,
  });

// Personalized topic chips (the viewer's lowest-scoring subdomains).
export const getContentFilterOptionsOptions = (includeAllTopics = false) =>
  queryOptions({
    queryKey: [queryKeys.content.filterOptions, includeAllTopics],
    queryFn: () => getContentFilterOptions(includeAllTopics),
    staleTime: CONTENT_STALE_TIME,
    gcTime: CONTENT_GC_TIME,
  });

// Text search — deterministic, so no draw semantics. The raw query is part of the query
// key (in-memory only); it must not reach the URL, analytics, or error reporting.
export const getContentSearchInfiniteOptions = (q: string, filters: TFilteredContentFilters = {}) => {
  const sanitized = sanitizeSearchQuery(q);

  return infiniteQueryOptions({
    queryKey: [queryKeys.content.search, sanitized, ...feedFilterKey(filters), filters.creator ?? 'all'],
    queryFn: ({ pageParam }) => getContentSearch({ q: sanitized, page: pageParam, limit: FEED_PAGE_SIZE, ...filters }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const p = lastPage.pagination;
      if (!p) return undefined;
      return p.page < p.totalPages ? p.page + 1 : undefined;
    },
    // Below the backend's post-sanitization minimum the request is a guaranteed 400.
    enabled: sanitized.length >= SEARCH_MIN_LENGTH,
    staleTime: CONTENT_STALE_TIME,
    gcTime: CONTENT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

// The focused "loop" stream, seeded by the tapped card. Read-only: fetching records
// nothing — a real view is reported separately via POST /focused-view.
// Unlike the browse feed, /focused-feed takes no `page` parameter — so there is no
// page-based fallback here. Continuation follows the opaque `nextCursor` and stops when
// it's null (the end of the draw).
export const getFocusedFeedInfiniteOptions = ({ anchorId, tab, limit }: TGetFocusedFeedParams = {}) =>
  infiniteQueryOptions({
    queryKey: [queryKeys.content.focusedFeed, anchorId ?? 'none', tab ?? 'ALL', limit],
    queryFn: ({ pageParam }) => getFocusedFeed({ anchorId, tab, limit, cursor: pageParam.cursor }),
    initialPageParam: {} as TFeedPageParam,
    getNextPageParam: (lastPage: TPersonalizedFeedResponse): TFeedPageParam | undefined => {
      const cursor = readNextCursor(lastPage);
      return cursor ? { cursor } : undefined;
    },
    // Without an anchor the backend has no seed for a new session.
    enabled: Boolean(anchorId),
    staleTime: CONTENT_STALE_TIME,
    gcTime: CONTENT_GC_TIME,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

type TAverageScoresData = {
  mental: number;
  spiritual: number;
  vocational: number;
  fun: number;
  purpose: number;
  social: number;
  physical: number;
  totalScore?: number;
};

export const getAssessmentsResultLastScore = () =>
  queryOptions({
    queryKey: ['assessments-result-last-score'],
    queryFn: async (): Promise<TAverageScoresData> => {
      const res = await api.get('/v1/assessments/result/last');
      // TODO: If you think that be can return status 200 but "success": false, you can throw an error here
      const wellnessData = res.data.data;

      for (const key in wellnessData) {
        if (typeof wellnessData[key as keyof THealthDomainScores] == 'number' && key !== 'totalScore') {
          wellnessData[key as keyof THealthDomainScores] *= WELLNESS_DATA_MULTIPLIER;
        }
      }

      return wellnessData;
    },
  });

export const getAssessmentsResultAverageScoresOrganization = () =>
  queryOptions({
    queryKey: ['assessments-result-average-scores-organization'],
    queryFn: async (): Promise<TAverageScoresData> => {
      const res = await api.get('/v1/assessments/result/average-scores/organization');
      // TODO: If you think that be can return status 200 but "success": false, you can throw an error here
      const wellnessData = res.data.data;

      for (const key in wellnessData) {
        if (typeof wellnessData[key as keyof THealthDomainScores] == 'number') {
          wellnessData[key as keyof THealthDomainScores] *= WELLNESS_DATA_MULTIPLIER;
        }
      }

      return wellnessData;
    },
  });

export const getOrganizationDashboard = (userId?: string) =>
  queryOptions({
    queryKey: ['admin-organization-dashboard', userId],
    queryFn: async (): Promise<TOrganizationDashboard> => {
      const { data: response } = await api.get<{ data: TOrganizationDashboard }>('/v1/organization/dash/dashboard');

      const feData = response.data;
      const healthDomainScores = feData.score.domains;

      for (const key in healthDomainScores) {
        if (typeof healthDomainScores[key as keyof THealthDomainScores] == 'number' && key !== 'totalScore') {
          healthDomainScores[key as keyof THealthDomainScores] *= WELLNESS_DATA_MULTIPLIER;
        }
      }

      feData.score.domains = healthDomainScores;

      return feData;
    },
  });
